import { useEffect } from "react"
import { Link } from "react-router-dom"
import FooterBranding from "./components/branding/FooterBranding"
import { Calendar, MessageSquare, Zap, Users, Globe, CheckCircle, Shield, Sparkles, Gift, BellRing, MoonStar, Check, Smartphone, QrCode, Share2, ClipboardList, Unplug, FileX } from "lucide-react"
import turnlinkLogo from './assets/turnlinkLOGO.png';
import { SpinnerCarga } from "./components/SpinnerCarga";
import './branding-animations.css';
import './BrandingPage.css';

// Landing pública. Tema claro de marca (salvia + verde del logo + Fredoka), ver
// styles/brand.css. Todo el estilo vive en BrandingPage.css; aquí solo estructura y copy.

const FEATURES = [
  {
    icon: MessageSquare,
    title: 'Chatbot inteligente',
    desc: 'Los clientes escriben por WhatsApp y el bot los guía paso a paso para gestionar sus citas.',
  },
  {
    icon: Calendar,
    title: 'Sincronización con Google Calendar',
    desc: 'Todas las citas se actualizan automáticamente en tu calendario, sin esfuerzo manual.',
    tone: 'calendar',
  },
  {
    icon: Globe,
    title: 'Página web personalizable',
    desc: 'Ofrece una web de reservas simple y efectiva para que tus clientes agenden online.',
  },
  {
    icon: Zap,
    title: 'Respuestas automáticas',
    desc: 'Configura respuestas personalizadas para preguntas frecuentes y horarios fuera de servicio.',
  },
  {
    icon: Users,
    title: 'Escalable para equipos',
    desc: 'Añade más barber@s o servicios con planes flexibles.',
  },
  {
    icon: CheckCircle,
    title: 'Fácil de usar',
    desc: 'Configura todo en minutos y enfócate en tu negocio, no en la administración.',
  },
];

const STEPS = [
  {
    title: 'Tu cliente escribe a tu WhatsApp',
    desc: 'Pide una cita como siempre. El bot reconoce que quiere agendar, cambiar o cancelar y le contesta al momento, a cualquier hora.',
  },
  {
    title: 'El bot ofrece horarios reales',
    desc: 'Revisa la agenda de Google Calendar de cada profesional y propone solo espacios libres. Si prefieres, manda el link de tu página de reservas.',
  },
  {
    title: 'Confirma y recuerda solo',
    desc: 'La cita queda en Google Calendar y se confirma por WhatsApp. Antes de la cita el bot manda el recordatorio para que no te dejen plantado.',
  },
  {
    title: 'Tú ves todo en tu panel',
    desc: 'Citas, ingresos, servicios más vendidos y desempeño por profesional, desde el celular.',
  },
];

// Lo que hace distinto al bot en el día a día (todo ya funciona así en el producto).
const BOT_RULES = [
  { icon: MoonStar, text: 'Tu WhatsApp sigue siendo tuyo: el bot solo contesta a quien pregunta por citas y se calla 2 horas cuando tú respondes a mano.' },
  { icon: BellRing, text: 'Recordatorios ilimitados, sin costo por mensaje.' },
  { icon: Users, text: 'Una agenda de Google Calendar por profesional, con su propio horario.' },
];

// Nombre comercial de los add-ons (aiAssistant, loyaltyCards en Subscription.constants).
const ADDONS_NAME = 'Extras';
const ADDONS = [
  {
    key: 'ai',
    icon: Sparkles,
    title: 'Asistente con IA',
    price: 200,
    desc: 'El bot entiende lenguaje natural: "¿tienes algo mañana en la tarde, lo más temprano posible?". Responde dudas de precios, horarios y ubicación con la información de tu negocio.',
    bullets: [
      'Nunca inventa una cita: solo agenda sobre tu calendario real.',
      'Si algo falla, el bot normal sigue atendiendo.',
      'Incluye hasta 1,500 conversaciones al mes.',
    ],
  },
  {
    key: 'loyalty',
    icon: Gift,
    title: 'Tarjetas de lealtad',
    price: 200,
    desc: 'Un sello por visita y recompensas por metas que tú defines: a las 5 visitas una cosa, a las 10 otra. El cliente la guarda en Google Wallet o la abre desde un link, sin instalar nada.',
    bullets: [
      'Registras la visita desde el panel con un toque.',
      'Las visitas acumuladas nunca se pierden.',
      'El cliente ve sus sellos y vales cuando quiera desde su link.',
    ],
  },
];

// Precios decididos el 2026-09-18 (vault: precios-y-competencia). PRICING en
// Subscription.constants.ts / pricing.js se actualiza al liberar a producción.
const PRICE = { base: 500, extraSeat: 200, addon: 200 };
const PLAN_FEATURES = [
  'Bot de WhatsApp 24/7 que agenda, cambia y cancela citas',
  'Recordatorios ilimitados antes de cada cita',
  '1 profesional con su Google Calendar (asientos extra a $200)',
  'Página de reservas con tu logo y colores',
  'Panel con ingresos, citas y desglose por servicio',
  'Respuestas automáticas a preguntas frecuentes',
];

const fmtMx = (n) => `$${n.toLocaleString('es-MX')}`;

// Mismo WhatsApp de soporte del pie de página.
const WA_LINK = 'https://wa.me/13605581156?text=' + encodeURIComponent('Hola, quiero probar TurnLink en mi negocio');

// Alta real: registro con código por WhatsApp y los 4 pasos de "Primeros pasos" del panel.
const SETUP_STEPS = [
  { icon: Smartphone, title: 'Crea tu cuenta', desc: 'Con un código que te llega por WhatsApp. Sin tarjeta.' },
  { icon: ClipboardList, title: 'Captura servicios y horarios', desc: 'Precios, duración y los días que trabaja cada quien.' },
  { icon: QrCode, title: 'Conecta Google Calendar y WhatsApp', desc: 'Escaneas un QR con tu WhatsApp de siempre, igual que WhatsApp Web.' },
  { icon: Share2, title: 'Comparte tu link', desc: 'El bot ya contesta y tu página de reservas ya recibe citas.' },
];

// Lo que hace que el negocio no dependa de nosotros (así funciona por diseño).
const TRUST = [
  { icon: Calendar, title: 'Tus citas viven en tu Google Calendar', desc: 'No en nuestra base. Si un día te vas, te quedas con toda tu agenda.' },
  { icon: Unplug, title: 'Tu WhatsApp de siempre', desc: 'No cambias de número ni pagas por mensaje. Desconectas cuando quieras desde el panel.' },
  { icon: FileX, title: 'Sin contrato ni permanencia', desc: 'Pagas mes a mes. Si dejas de pagar tienes 5 días de gracia antes de que el bot se detenga.' },
];

// Rangos de mercado en México para un profesional (sep 2026), sin nombrar competidores.
const MARKET = [
  { label: 'Agendas sin bot', price: '$300 a $650', desc: 'Calendario y reservas web. WhatsApp aparte o por mensaje.' },
  { label: 'TurnLink', price: fmtMx(PRICE.base), desc: 'Agenda por WhatsApp 24/7, recordatorios y Google Calendar.', highlight: true },
  { label: 'Bots con IA', price: '$1,000 a $3,200', desc: 'Agendan por chat, pero al precio de tres o seis meses de TurnLink.' },
];

// Las objeciones que salen en los mensajes directos, con la respuesta real.
const FAQ = [
  { q: '¿Necesito otro número de WhatsApp?', a: 'No. Se conecta a tu WhatsApp actual escaneando un QR desde el panel, como WhatsApp Web. Tus chats y contactos siguen igual.' },
  { q: '¿Y si yo contesto un mensaje?', a: 'El bot se calla dos horas con esa persona y te deja la conversación a ti. Solo vuelve a hablar si le vuelven a pedir cita.' },
  { q: '¿Le va a contestar a mis amigos y familia?', a: 'No. Solo responde a quien pregunta por citas, precios u horarios. Además puedes marcar números para que nunca les conteste.' },
  { q: '¿Funciona para mi giro?', a: 'Barberías, salones, uñas, spas y consultorios: cualquier negocio que agenda citas de una duración fija por profesional.' },
  { q: '¿Qué pasa si dejo de pagar?', a: 'Tienes 5 días de gracia y luego el bot deja de contestar. Tus citas siguen en tu Google Calendar y las tarjetas de lealtad de tus clientes se quedan visibles 90 días.' },
  { q: '¿Puedo agendar yo también?', a: 'Sí. Desde el panel, desde tu Google Calendar o por WhatsApp. El bot respeta lo que tú pongas y nunca empalma citas.' },
];

function BrandingPage() {
  useEffect(() => {
    document.getElementById('initial-loader')?.remove();

    // Agregar clase al body para ocultar footer estático
    document.body.classList.add('js-loaded');
    // El superadmin ya no vive aquí: tiene su ruta en /admin (ver AdminPage).
  }, []);

  // Scroll-reveal: fade-up sections/cards as they enter the viewport (once).
  useEffect(() => {
    const els = document.querySelectorAll('.reveal, .reveal-stagger');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -80px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="branding-root min-vh-100 d-flex flex-column">
      {/* Header/Navbar - Mobile First */}
      <nav className="branding-nav">
        <div className="app-container-wide branding-nav-inner">
          {/* Logo: ícono del bot + wordmark en Fredoka (turn tinta / link verde) */}
          <div className="d-flex align-items-center gap-3">
            <a href="/" className="tl-wordmark" aria-label="Turnlink, inicio">
              <img src={turnlinkLogo} alt="" />
              <span>turn<em>link</em></span>
            </a>
            <span
              className="nav-tagline d-none d-md-inline-flex"
              title="Turnlink: Sistema automático de citas por WhatsApp con sincronización a Google Calendar"
            >
              Citas por WhatsApp + Google Calendar
            </span>
          </div>

          {/* Acceso al superadmin (ruta propia, sesión persistente) */}
          <Link to="/admin" className="btn-nav-admin text-decoration-none">
            <Shield size={18} />
            <span className="d-none d-sm-inline">Admin</span>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow-1">
        {/* Hero: texto a la izquierda, vista previa del chat a la derecha */}
        <section className="branding-hero">
          <div className="hero-blob hero-blob-green" aria-hidden />
          <div className="hero-blob hero-blob-blue" aria-hidden />

          <div className="app-container-wide branding-hero-container">
            <div className="branding-hero-grid">
              <div className="branding-text-content">
                <span className="branding-eyebrow">WhatsApp + Google Calendar</span>
                <h1 className="branding-hero-title">
                  Automatiza tus citas{' '}
                  <br />
                  <span className="hero-highlight">por WhatsApp</span>
                </h1>
                <p className="branding-hero-description">
                  Transforma la gestión de tu negocio. Un chatbot inteligente
                  guía a tus clientes para agendar, cambiar o cancelar citas,
                  sincronizando todo automáticamente con Google Calendar.
                </p>

                <a href="/registro" className="btn-cta-hero">
                  <span>Comienza gratis</span>
                  <span className="btn-cta-icon" aria-hidden>→</span>
                </a>
              </div>

              {/* Vista previa del producto: la conversación real del bot en WhatsApp */}
              <div className="branding-phone-container">
                <div className="branding-phone">
                  <div className="branding-phone-screen">
                    <div className="branding-phone-header">
                      <img src={turnlinkLogo} alt="" className="phone-header-avatar" />
                      <div className="phone-header-text">
                        <span className="phone-header-name">Turnlink</span>
                        <span className="phone-header-status">en línea</span>
                      </div>
                    </div>

                    <div className="branding-chat">
                      <div className="chat-message user">
                        <div className="message-bubble">Hola, quiero agendar una cita</div>
                      </div>

                      <div className="chat-message bot">
                        <div className="message-bubble">¡Perfecto! Abre este link para elegir tu fecha y hora 👇</div>
                      </div>

                      <div className="chat-message bot">
                        <div className="message-bubble bubble-link">🔗 Agendar mi cita</div>
                      </div>

                      <div className="chat-message bot">
                        <div className="message-bubble bubble-waiting">Esperando que completes tu agendamiento... ⏳</div>
                      </div>

                      <div className="chat-message bot">
                        <div className="message-bubble bubble-success">✅ ¡Cita agendada exitosamente!</div>
                      </div>

                      <div className="chat-message bot">
                        <div className="message-bubble">📅 <strong>Mañana a las 3:00 PM</strong></div>
                      </div>

                      <div className="chat-message bot">
                        <div className="message-bubble">✂️ <strong>Corte de cabello</strong></div>
                      </div>

                      <div className="chat-message bot">
                        <div className="message-bubble bubble-sync">
                          <Calendar size={14} aria-hidden /> Sincronizado con Google Calendar
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features: lista en dos columnas, sin tarjetas */}
        <section id="features" className="branding-section">
          <div className="app-container-wide">
            <h2 className="section-title reveal">¿Por qué elegir Turnlink?</h2>
            <div className="features-grid reveal-stagger">
              {FEATURES.map((feature) => (
                <div key={feature.title} className={`feature-item ${feature.tone === 'calendar' ? 'is-calendar' : ''}`}>
                  <span className="feature-icon" aria-hidden><feature.icon size={22} /></span>
                  <div>
                    <h5>{feature.title}</h5>
                    <p>{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cómo funciona: tres pasos con línea que los une */}
        <section id="howitworks" className="branding-section branding-section-alt">
          <div className="app-container-wide">
            <h2 className="section-title reveal">Cómo funciona la automatización</h2>
            <ol className="steps-grid reveal-stagger">
              {STEPS.map((step, idx) => (
                <li key={step.title} className="step-item">
                  <span className="step-number" aria-hidden>{idx + 1}</span>
                  <h5>{step.title}</h5>
                  <p>{step.desc}</p>
                </li>
              ))}
            </ol>
            <ul className="bot-rules reveal">
              {BOT_RULES.map((rule) => (
                <li key={rule.text}>
                  <rule.icon size={18} aria-hidden />
                  <span>{rule.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Extras: los dos add-ons, cada uno con una vista previa real del producto */}
        <section id="extras" className="branding-section">
          <div className="app-container-wide">
            <h2 className="section-title reveal">{ADDONS_NAME} que suman a tu base</h2>
            <p className="section-lead reveal">Cada uno cuesta {fmtMx(PRICE.addon)} al mes y se activa o se quita con tu siguiente pago.</p>
            <div className="addons-grid reveal-stagger">
              {ADDONS.map((addon) => (
                <article key={addon.key} className={`addon-card addon-${addon.key}`}>
                  <div className="addon-body">
                    <span className="addon-icon" aria-hidden><addon.icon size={22} /></span>
                    <h3>{addon.title}</h3>
                    <p>{addon.desc}</p>
                    <ul className="addon-bullets">
                      {addon.bullets.map((b) => <li key={b}><Check size={16} aria-hidden />{b}</li>)}
                    </ul>
                    <span className="addon-price">+{fmtMx(addon.price)}<small>/mes</small></span>
                  </div>

                  {addon.key === 'ai' ? (
                    <div className="addon-preview addon-chat" aria-label="Ejemplo de conversación con el asistente">
                      <div className="chat-message user"><div className="message-bubble">¿Tienes algo mañana en la tarde? Lo más temprano que se pueda</div></div>
                      <div className="chat-message bot"><div className="message-bubble">Sí: mañana a las 4:00 pm con Luis, corte de cabello $150. ¿Te lo agendo?</div></div>
                      <div className="chat-message user"><div className="message-bubble">Va</div></div>
                      <div className="chat-message bot"><div className="message-bubble bubble-success">✅ Listo, mañana 4:00 pm con Luis. Te mando un recordatorio antes.</div></div>
                    </div>
                  ) : (
                    <div className="addon-preview addon-wallet" aria-label="Ejemplo de tarjeta de lealtad">
                      <div className="wallet-card">
                        <div className="wallet-head">
                          <span className="wallet-biz">Barbería El Corte</span>
                          <span className="wallet-tag">Tarjeta de lealtad</span>
                        </div>
                        <div className="wallet-stamps" aria-hidden>
                          {[1, 2, 3, 4, 5, 6].map((n) => <span key={n} className={n <= 4 ? 'is-done' : ''}>{n <= 4 ? <Check size={14} /> : n}</span>)}
                        </div>
                        <div className="wallet-foot">
                          <span>4 de 6 visitas</span>
                          <span>Meta: corte gratis</span>
                        </div>
                      </div>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Datos: vista previa de la pestaña Ingresos del panel */}
        <section id="datos" className="branding-section branding-section-alt">
          <div className="app-container-wide">
            <div className="data-grid">
              <div className="data-text reveal">
                <h2 className="section-title">Sabes cuánto entra y por qué</h2>
                <p>
                  Cada cita que agenda el bot queda registrada. Tu panel te dice cuánto facturaste en el mes,
                  cuántas citas fueron, el ticket promedio, qué servicios se venden más y cómo va cada profesional.
                  Sin capturar nada a mano.
                </p>
                <ul className="data-list">
                  <li><Check size={16} aria-hidden />Ingresos por semana y por mes</li>
                  <li><Check size={16} aria-hidden />Desglose por servicio y por profesional</li>
                  <li><Check size={16} aria-hidden />Citas confirmadas, canceladas y recordatorios enviados</li>
                </ul>
              </div>
              <div className="panel-preview reveal" aria-label="Ejemplo de la pestaña Ingresos del panel">
                <div className="panel-preview-head">
                  <span>Ingresos</span>
                  <span className="panel-preview-period">Este mes</span>
                </div>
                <div className="panel-kpis">
                  <div className="panel-kpi is-main"><span>Ingresos</span><strong>$18,420</strong></div>
                  <div className="panel-kpi"><span>Citas</span><strong>172</strong></div>
                  <div className="panel-kpi"><span>Ticket promedio</span><strong>$107</strong></div>
                </div>
                <div className="panel-bars" aria-hidden>
                  {[62, 78, 71, 88].map((h, i) => <span key={i} style={{ '--h': `${h}%` }}><i>S{i + 1}</i></span>)}
                </div>
                <div className="panel-services" aria-hidden>
                  {[['Corte de cabello', 64], ['Corte y barba', 26], ['Barba', 10]].map(([name, pct]) => (
                    <div key={name} className="panel-service"><span>{name}</span><b>{pct}%</b><i style={{ '--w': `${pct}%` }} /></div>
                  ))}
                </div>
                <p className="panel-preview-note">Datos de ejemplo</p>
              </div>
            </div>
          </div>
        </section>

        {/* Alta en 20 minutos: checklist + oferta de configurarlo por WhatsApp */}
        <section id="setup" className="branding-section">
          <div className="app-container-wide">
            <div className="setup-grid">
              <div className="setup-text reveal">
                <h2 className="section-title">Listo en 20 minutos</h2>
                <ol className="setup-steps">
                  {SETUP_STEPS.map((step) => (
                    <li key={step.title}>
                      <span className="setup-icon" aria-hidden><step.icon size={20} /></span>
                      <div>
                        <h5>{step.title}</h5>
                        <p>{step.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="setup-offer reveal">
                <span className="setup-offer-icon" aria-hidden><MessageSquare size={26} /></span>
                <h3>¿Prefieres que lo haga yo?</h3>
                <p>Escríbeme por WhatsApp y te lo dejo configurado en una llamada de 20 minutos, con tus servicios y horarios cargados. Sin costo.</p>
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                  <MessageSquare size={18} aria-hidden /> Escríbeme por WhatsApp
                </a>
                <span className="setup-offer-note">David, fundador de TurnLink. Oaxaca, México.</span>
              </div>
            </div>
          </div>
        </section>

        {/* Precios: un plan, asientos y Extras */}
        <section id="pricing" className="branding-section">
          <div className="app-container-wide">
            <h2 className="section-title reveal">Un solo plan, sin contrato</h2>
            <div className="pricing-grid">
              <div className="pricing-card reveal">
                <div className="pricing-content">
                  <div>
                    <h4 className="pricing-plan">Plan Base</h4>
                    <p className="pricing-price">{fmtMx(PRICE.base)}<small>/mes</small></p>
                    <p className="pricing-note">+ IVA si requieres factura. El primer mes es gratis.</p>
                  </div>

                  <ul className="pricing-features">
                    {PLAN_FEATURES.map((feature) => (
                      <li key={feature}>
                        <CheckCircle size={18} aria-hidden />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a href="/registro" className="btn-pricing">Comienza gratis</a>
                  <p className="pricing-fine">Pago mensual por transferencia. Cancelas cuando quieras.</p>
                </div>
              </div>

              <aside className="pricing-side reveal">
                <div className="pricing-line">
                  <div>
                    <h4>Profesional extra</h4>
                    <p>Cada barbero o estilista con su propia agenda de Google Calendar y su horario.</p>
                  </div>
                  <span className="pricing-line-price">+{fmtMx(PRICE.extraSeat)}<small>/mes</small></span>
                </div>
                {ADDONS.map((addon) => (
                  <div key={addon.key} className="pricing-line">
                    <div>
                      <h4>{addon.title}</h4>
                      <p>{addon.key === 'ai' ? 'Lenguaje natural y respuestas con la información de tu negocio.' : 'Sellos, recompensas y tarjeta en Google Wallet.'}</p>
                    </div>
                    <span className="pricing-line-price">+{fmtMx(addon.price)}<small>/mes</small></span>
                  </div>
                ))}
                <p className="pricing-total">
                  Todo incluido, con IA y lealtad: <strong>{fmtMx(PRICE.base + PRICE.addon * 2)}/mes</strong> para un profesional.
                </p>
              </aside>
            </div>

            <div className="market reveal" aria-label="Comparación con el mercado">
              <p className="market-title">Lo que cuesta agendar en México, por profesional al mes</p>
              <div className="market-grid">
                {MARKET.map((m) => (
                  <div key={m.label} className={`market-item ${m.highlight ? 'is-us' : ''}`}>
                    <span className="market-label">{m.label}</span>
                    <span className="market-price">{m.price}</span>
                    <span className="market-desc">{m.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Confianza: por qué no dependes de nosotros */}
        <section id="confianza" className="branding-section branding-section-alt">
          <div className="app-container-wide">
            <h2 className="section-title reveal">Tus datos son tuyos</h2>
            <div className="trust-grid reveal-stagger">
              {TRUST.map((t) => (
                <div key={t.title} className="trust-item">
                  <span className="trust-icon" aria-hidden><t.icon size={22} /></span>
                  <h5>{t.title}</h5>
                  <p>{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Preguntas frecuentes: las objeciones reales */}
        <section id="faq" className="branding-section">
          <div className="app-container-wide">
            <h2 className="section-title reveal">Preguntas frecuentes</h2>
            <dl className="faq-grid reveal-stagger">
              {FAQ.map((item) => (
                <div key={item.q} className="faq-item">
                  <dt>{item.q}</dt>
                  <dd>{item.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>

      {/* Footer */}
      <FooterBranding />
      <SpinnerCarga />
    </div>
  )
}


export default BrandingPage;
